import { fetchReportByIdWithNotReadyPolling } from '@ltvco/refresh-lib/hooks';
import {
  addressSearch,
  mapResidentsToResidentInfo,
  personSearch,
  PersonTeaser,
  phoneSearch,
  ReportFactory,
  searchPersonResults,
  type PersonReport,
  type PhoneReport,
  type PropertyReport,
  peopleTypes,
  propertiesTypes,
  serializedTypes,
} from '@ltvco/refresh-lib/v1';

import { bestPhone } from './bestPhone';
import { shouldRunPhoneReport } from './featureFlags';

import type { QueryClient } from '@ltvco/refresh-lib/vendors';

export type OwnerResolution = 'direct' | 'teaser' | 'not_found';

export interface ResolveLeadReportsParams {
  fullName?: string;
  address: string;
  city?: string;
  state?: string;
  zipcode?: string;
  brandLink: string;
  queryClient: QueryClient;
}

export const reportQueryKey = (permalink: string) => ['report', permalink];

const propertySearchQueryKey = (params: {
  address: string;
  city?: string;
  state?: string;
  zipcode?: string;
}) => ['leadScore', 'propertySearch', params];

const personSearchQueryKey = (bvid: string) => [
  'leadScore',
  'personSearch',
  bvid,
];

const phoneSearchQueryKey = (phone: string) => [
  'leadScore',
  'phoneSearch',
  phone,
];

export interface ResolvedLeadReports {
  propertyReport: PropertyReport;
  propertyPermalink: string;
  subjectProperty: serializedTypes.SerializedProperty | undefined;
  personReport: PersonReport | null;
  personPermalink?: string;
  phoneReport: PhoneReport | null;
  phonePermalink?: string;
  resolvedOwnerName?: string;
  ownerResolution: OwnerResolution;
}

/**
 * A property search can return multiple candidate records for the same
 * address (e.g. distinct parcels/units, or a provider's disambiguation
 * options) — always take the first one as the subject property.
 */
const selectSubjectProperty = (
  properties: serializedTypes.SerializedProperty[]
): serializedTypes.SerializedProperty | undefined => properties[0];

const normalizeNameWords = (name: string): string[] =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s-]/g, '')
    .split(/\s+/)
    .filter(Boolean);

const namesMatch = (a: string, b: string): boolean => {
  const aWords = normalizeNameWords(a);
  const bWords = normalizeNameWords(b);
  if (!aWords.length || !bWords.length) {
    return false;
  }
  return (
    aWords[0] === bWords[0] &&
    aWords[aWords.length - 1] === bWords[bWords.length - 1]
  );
};

const stripAgeSuffix = (name: string): string =>
  name.replace(/,\s*\d+\s*$/, '').trim();

const normalizeOwnerIdentity = (
  owner: peopleTypes.Identity | peopleTypes.FullPerson
): peopleTypes.Identity => ('identity' in owner ? owner.identity : owner);

const locationMatches = (
  locations: (string | undefined)[] | undefined,
  city?: string,
  state?: string
): boolean => {
  if (!city || !state || !locations?.length) {
    return false;
  }
  const target = `${city}, ${state}`.toLowerCase();
  return locations.some((location) => location?.toLowerCase() === target);
};

async function resolveOwnerBvid(
  owners: peopleTypes.Identities,
  residents: propertiesTypes.Residents,
  brandLink: string
): Promise<{
  bvid: string;
  ownerName: string;
  resolution: OwnerResolution;
} | null> {
  const residentInfos = mapResidentsToResidentInfo(residents);

  for (const owner of owners) {
    const ownerName = owner.names?.[0]?.full ?? '';
    const directBvid = owner.ids?.[0];
    if (ownerName && directBvid) {
      return { bvid: directBvid, ownerName, resolution: 'direct' };
    }
  }

  for (const owner of owners) {
    const ownerName = owner.names?.[0]?.full ?? '';
    if (!ownerName) {
      continue;
    }

    const residentMatch = residentInfos.find((info) =>
      namesMatch(ownerName, info.fullName)
    );
    const { city, state } = residentMatch?.personSearch ?? {};
    if (!residentMatch || !city || !state) {
      continue;
    }

    try {
      const teaserData = await searchPersonResults(
        {
          searchPersonBy: 'name',
          options: { personSearchParams: residentMatch.personSearch },
        },
        brandLink
      );
      const records = new PersonTeaser(teaserData).data;

      const matches = records.filter(
        (record) =>
          locationMatches(record.locations, city, state) &&
          namesMatch(ownerName, stripAgeSuffix(record.name ?? ''))
      );

      if (matches.length === 1 && matches[0].bvid) {
        return { bvid: matches[0].bvid, ownerName, resolution: 'teaser' };
      }
    } catch {
      continue;
    }
  }

  return null;
}

export async function resolveLeadReports({
  fullName,
  address,
  city,
  state,
  zipcode,
  brandLink,
  queryClient,
}: ResolveLeadReportsParams): Promise<ResolvedLeadReports> {
  const propertySearchResult = await queryClient.fetchQuery({
    queryKey: propertySearchQueryKey({ address, city, state, zipcode }),
    queryFn: () => addressSearch({ address, city, state, zipcode }),
  });
  const propertyPermalink = propertySearchResult?.report?.permalink;
  if (!propertyPermalink) {
    throw new Error('No property record found for the selected address.');
  }
  const propertyRawData = await queryClient.fetchQuery({
    queryKey: reportQueryKey(propertyPermalink),
    queryFn: () =>
      fetchReportByIdWithNotReadyPolling(propertyPermalink, {
        skipRedirectOnMovedPermanently: true,
      }),
  });
  const propertyReport = ReportFactory.create(propertyRawData, 'property');

  const subjectProperty = selectSubjectProperty(
    propertyReport.data.properties ?? []
  );

  const owners = (subjectProperty?.owners ?? []).map(normalizeOwnerIdentity);
  const residents = subjectProperty?.residents ?? [];

  const candidateOwners = fullName
    ? owners.filter((owner) =>
        namesMatch(fullName, owner.names?.[0]?.full ?? '')
      )
    : owners;

  const resolvedOwner = await resolveOwnerBvid(
    candidateOwners,
    residents,
    brandLink
  );

  let personReport: PersonReport | null = null;
  let personPermalink: string | undefined;

  if (resolvedOwner) {
    try {
      const personSearchResult = await queryClient.fetchQuery({
        queryKey: personSearchQueryKey(resolvedOwner.bvid),
        queryFn: () => personSearch({ bvid: resolvedOwner.bvid }),
      });
      const resolvedPersonPermalink = personSearchResult.report.permalink;
      const personRawData = await queryClient.fetchQuery({
        queryKey: reportQueryKey(resolvedPersonPermalink),
        queryFn: () =>
          fetchReportByIdWithNotReadyPolling(resolvedPersonPermalink, {
            skipRedirectOnMovedPermanently: true,
          }),
      });
      personPermalink = resolvedPersonPermalink;
      personReport = ReportFactory.create(personRawData, 'person');
    } catch {
      personReport = null;
      personPermalink = undefined;
    }
  }

  let phoneReport: PhoneReport | null = null;
  let phonePermalink: string | undefined;

  if (shouldRunPhoneReport) {
    const ownerPhone = personReport?.getOwner({
      potentialOwnerIndex: 0,
      higherConfidenceThreshold: 0,
      showHighConfidenceDataOnly: false,
    })?.phones;
    const bestOwnerPhone = bestPhone(ownerPhone)?.number;

    if (bestOwnerPhone) {
      try {
        const phoneSearchResult = await queryClient.fetchQuery({
          queryKey: phoneSearchQueryKey(bestOwnerPhone),
          queryFn: () => phoneSearch({ phone: bestOwnerPhone }),
        });
        const resolvedPhonePermalink = phoneSearchResult.report.permalink;
        const phoneRawData = await queryClient.fetchQuery({
          queryKey: reportQueryKey(resolvedPhonePermalink),
          queryFn: () =>
            fetchReportByIdWithNotReadyPolling(resolvedPhonePermalink, {
              skipRedirectOnMovedPermanently: true,
            }),
        });
        phonePermalink = resolvedPhonePermalink;
        phoneReport = ReportFactory.create(phoneRawData, 'phone');
      } catch {
        phoneReport = null;
        phonePermalink = undefined;
      }
    }
  }

  return {
    propertyReport,
    propertyPermalink,
    subjectProperty,
    personReport,
    personPermalink,
    phoneReport,
    phonePermalink,
    resolvedOwnerName: resolvedOwner?.ownerName,
    ownerResolution: resolvedOwner?.resolution ?? 'not_found',
  };
}

export interface FetchLeadReportsByPermalinksParams {
  propertyPermalink: string;
  personPermalink?: string;
  phonePermalink?: string;
  queryClient: QueryClient;
}

export interface FetchedLeadReports {
  propertyReport: PropertyReport;
  subjectProperty: serializedTypes.SerializedProperty;
  personReport: PersonReport | null;
  phoneReport: PhoneReport | null;
}

async function fetchOptionalPersonReport(
  permalink: string | undefined,
  queryClient: QueryClient
): Promise<PersonReport | null> {
  if (!permalink) {
    return null;
  }
  try {
    const rawData = await queryClient.fetchQuery({
      queryKey: reportQueryKey(permalink),
      queryFn: () =>
        fetchReportByIdWithNotReadyPolling(permalink, {
          skipRedirectOnMovedPermanently: true,
        }),
    });
    return ReportFactory.create(rawData, 'person');
  } catch {
    return null;
  }
}

async function fetchOptionalPhoneReport(
  permalink: string | undefined,
  queryClient: QueryClient
): Promise<PhoneReport | null> {
  if (!permalink) {
    return null;
  }
  try {
    const rawData = await queryClient.fetchQuery({
      queryKey: reportQueryKey(permalink),
      queryFn: () =>
        fetchReportByIdWithNotReadyPolling(permalink, {
          skipRedirectOnMovedPermanently: true,
        }),
    });
    return ReportFactory.create(rawData, 'phone');
  } catch {
    return null;
  }
}

export async function fetchLeadReportsByPermalinks({
  propertyPermalink,
  personPermalink,
  phonePermalink,
  queryClient,
}: FetchLeadReportsByPermalinksParams): Promise<FetchedLeadReports> {
  const propertyRawData = await queryClient.fetchQuery({
    queryKey: reportQueryKey(propertyPermalink),
    queryFn: () =>
      fetchReportByIdWithNotReadyPolling(propertyPermalink, {
        skipRedirectOnMovedPermanently: true,
      }),
  });
  const propertyReport = ReportFactory.create(propertyRawData, 'property');
  const subjectProperty = selectSubjectProperty(
    propertyReport.data.properties ?? []
  );
  if (!subjectProperty) {
    throw new Error('No property record found for this report.');
  }

  const [personReport, phoneReport] = await Promise.all([
    fetchOptionalPersonReport(personPermalink, queryClient),
    shouldRunPhoneReport
      ? fetchOptionalPhoneReport(phonePermalink, queryClient)
      : Promise.resolve(null),
  ]);

  return { propertyReport, subjectProperty, personReport, phoneReport };
}
